import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Typography,
  useTheme,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import { AxiosService } from "../../services/axios/axiosService";

// Helper to format seconds as M:SS (e.g. 3:45)
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "0:00";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = (total % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

// Status from details API response:
// - If there is meaningful speech and/or duration, treat as Answered
// - If there is no speech (e.g. "[No speech could be recognized]" or recognition timeout)
//   or zero duration, show as "Not answered".
const getStatusMeta = (response) => {
  const duration = response?.responseDurationSeconds ?? 0;
  const text = response?.transcribedText || "";

  const isNoSpeech =
    typeof text === "string" &&
    /(No speech could be recognized|No speech|Recognition timeout)/i.test(text);

  const hasSpeech = !isNoSpeech && text.trim().length > 0;

  if (duration > 0 && hasSpeech) {
    return {
      label: "Answered",
      color: "#22c55e",
      bg: "#b6f7cc",
      border: "#22c55e",
    };
  }

  return {
    label: "Not answered",
    color: "#dc2626",
    bg: "#fee2e2",
    border: "#fca5a5",
  };
};

// Persists across Strict Mode remounts so auto-play runs only once per load
let lastAutoPlayedKey = null;

const InterviewQuestionsCard = ({
  loading,
  error,
  questions,
  totalQuestions,
  selectedResponseId,
  onSelectResponse,
  onTtsEnd,
  onTtsStart,
  registerStopTts,
}) => {
  const theme = useTheme();
  const responses = Array.isArray(questions) ? questions : [];
  const answeredCount = responses.filter(
    (resp) => getStatusMeta(resp).label === "Answered",
  ).length;
  const pageSize = 5;
  const [page, setPage] = React.useState(0);
  const totalPages = Math.max(1, Math.ceil(responses.length / pageSize));

  // // Reset to first page when question set changes
  // React.useEffect(() => {
  //   setPage(0);
  // }, [questions]);
   // -------------------------------
  // 🔊 TEXT TO SPEECH LOGIC
  // -------------------------------

  const audioRef = React.useRef(null);
  const autoPlayedRef = React.useRef(false);
  const ttsAbortControllerRef = React.useRef(null);
  const ttsLatestRequestIdRef = React.useRef(null);
  const [ttsPlayingResponseId, setTtsPlayingResponseId] = React.useState(null);

  const stopTts = React.useCallback(() => {
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    ttsLatestRequestIdRef.current = null;
    setTtsPlayingResponseId(null);
  }, []);

  // Let parent capture stopTts so it can stop TTS when video starts playing
  React.useEffect(() => {
    if (typeof registerStopTts === "function") {
      registerStopTts(stopTts);
    }
  }, [registerStopTts, stopTts]);

  const callTextToSpeech = React.useCallback(
    async (text, responseId) => {
      if (!text) return;

      // Inform parent that TTS is about to start so it can pause the video
      if (typeof onTtsStart === "function") {
        onTtsStart();
      }

      // Abort any in-flight TTS request so only the latest plays
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      const requestId = responseId ?? Symbol("tts");
      ttsLatestRequestIdRef.current = requestId;
      setTtsPlayingResponseId(responseId ?? null);

      const controller = new AbortController();
      ttsAbortControllerRef.current = controller;

      try {
        const body = { Text: text };
        const response = await AxiosService.post(
          `/api/Interview/text-to-speech`,
          body,
          { responseType: "blob", signal: controller.signal }
        );

        // Only play if this is still the latest request (user didn’t click another question)
        const audioUrl = window.URL.createObjectURL(response);
        if (ttsLatestRequestIdRef.current !== requestId) {
          URL.revokeObjectURL(audioUrl);
          return;
        }
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          ttsLatestRequestIdRef.current = null;
          setTtsPlayingResponseId(null);
          if (typeof onTtsEnd === "function") {
            onTtsEnd();
          }
        };

        await audio.play();
      } catch (err) {
        if (controller.signal?.aborted) return;
        console.error("TTS error:", err);
        if (ttsLatestRequestIdRef.current === requestId) {
          ttsLatestRequestIdRef.current = null;
          setTtsPlayingResponseId(null);
          if (typeof onTtsEnd === "function") {
            onTtsEnd();
          }
        }
      } finally {
        if (ttsAbortControllerRef.current === controller) {
          ttsAbortControllerRef.current = null;
        }
      }
    },
    [onTtsEnd, onTtsStart]
  );

  // Reset page when question set changes; different interview will have different first response id so auto-play runs again
  React.useEffect(() => {
    setPage(0);
    autoPlayedRef.current = false;
  }, [questions]);

  // When user leaves the page (route change), stop any playing audio and abort in-flight TTS
  React.useEffect(() => {
    return () => {
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      ttsLatestRequestIdRef.current = null;
      lastAutoPlayedKey = null;
    };
  }, []);

  // ✅ Auto play first question once after load. Module-level key prevents double play on Strict Mode remount.
  React.useEffect(() => {
    if (loading || responses.length === 0) return;
    const firstResponse = responses[0];
    const sessionKey = firstResponse?.id ?? firstResponse?.question?.id ?? "";
    if (lastAutoPlayedKey === sessionKey) return;
    lastAutoPlayedKey = sessionKey;
    autoPlayedRef.current = true;
    if (!selectedResponseId) {
      onSelectResponse?.(firstResponse);
    }
    const questionText = firstResponse?.question?.questionText;
    if (questionText) callTextToSpeech(questionText, firstResponse?.id);
  }, [loading, responses, callTextToSpeech]);


  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleResponses = responses.slice(startIndex, endIndex);
  const isEmptyState = !loading && !error && responses.length === 0;

  return (
    <Card
      elevation={2}
      sx={{
        
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, mb:1 }}
          >
            IGNA Scout Questions
          </Typography> 
          {!loading && !error && responses.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totalQuestions != null
                ? `${answeredCount} of ${totalQuestions} answered`
                : `${answeredCount} answered`}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            // Keep a consistent visual height roughly equal to
            // five question rows, so the layout stays stable.
            minHeight: 320,
            display: isEmptyState ? "flex" : "block",
            alignItems: isEmptyState ? "center" : "stretch",
            justifyContent: isEmptyState ? "center" : "flex-start",
          }}
        >
          {loading && (
            <Box>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {!loading && !error && responses.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              No question details available.
            </Typography>
          )}

          {!loading &&
            !error &&
            visibleResponses.map((response, index) => {
            const question = response?.question || {};
              const absoluteIndex = startIndex + index;
              const number = absoluteIndex + 1;
            const text = question.questionText || "Untitled question";
            const seconds = response.responseDurationSeconds ?? 0;
            const durationLabel = formatDuration(seconds);
            const statusMeta = getStatusMeta(response);

            const isSelected = selectedResponseId != null && response?.id === selectedResponseId;

            const isTtsPlaying = ttsPlayingResponseId != null && response?.id === ttsPlayingResponseId;

            return (
              <Box
                key={response?.id ?? absoluteIndex}
                onClick={() => {
                  onSelectResponse?.(response);
                  callTextToSpeech(text, response?.id);
                }}
                role="button"
                tabIndex={0}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1,
                  p: 1,
                  borderRadius: 6,
                  cursor: onSelectResponse ? "pointer" : "default",
                  border: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  backgroundColor: isSelected
                    ? theme.palette.mode === "dark"
                      ? "rgba(99, 102, 241, 0.15)"
                      : "rgba(99, 102, 241, 0.08)"
                    : theme.palette.mode === "dark"
                    ? "rgba(15,23,42,0.6)"
                    : "#f9fafb",
                  "&:hover": onSelectResponse
                    ? {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "#f3f4f6",
                      }
                    : {},
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: statusMeta.bg,
                      color: statusMeta.color,
                    }}
                  >
                    {number}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {text}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mt: 0.25,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {durationLabel} •{" "}
                        <Chip
                          label={statusMeta.label}
                          size="small"
                          sx={{
                            fontSize: "0.7rem",
                            backgroundColor: statusMeta.bg,
                            color: statusMeta.color,
                            fontWeight: 500,
                            flexShrink: 0,
                          }}
                        />
                      </Typography>
                      <Tooltip title={isTtsPlaying ? "Stop speech" : "Play question (TTS)"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isTtsPlaying) {
                              stopTts();
                            } else {
                              onSelectResponse?.(response);
                              callTextToSpeech(text, response?.id);
                            }
                          }}
                          sx={{
                            p: 0.25,
                            color: isTtsPlaying ? "error.main" : theme.palette.primary.main,
                          }}
                          aria-label={isTtsPlaying ? "Stop speech" : "Play question"}
                        >
                          {isTtsPlaying ? (
                            <StopIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <VolumeUpIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {!loading && !error && responses.length > pageSize && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 3,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Typography variant="caption" color="text.secondary">
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default InterviewQuestionsCard;

